import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import NavBar from "../components/NavBar";

const STATUS_BADGE = {
  pending_payment: ["badge-pending", "Awaiting payment"],
  paid:            ["badge-paid", "Paid · intake pending"],
  intake_received: ["badge-default", "Intake received · generating"],
  report_ready:    ["badge-ready", "Report ready · in review"],
  released:        ["badge-released", "Report released"],
  cancelled:       ["badge-default", "Cancelled"],
};

export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [services, setServices] = useState([]);
  const [orders, setOrders] = useState([]);
  const [busyId, setBusyId] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!user) { nav("/login"); return; }
    api.get("/services").then((r) => setServices(r.data));
    refresh();
    // eslint-disable-next-line
  }, [user]);

  const refresh = useCallback(() => {
    api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
  }, []);

  const buy = useCallback(async (serviceId) => {
    setBusyId(serviceId); setErr("");
    try {
      const ord = await api.post("/orders", { service_id: serviceId });
      const co = await api.post(`/orders/${ord.data.id}/checkout`, { origin_url: window.location.origin });
      window.location.href = co.data.url;
    } catch (e) {
      setErr(e?.response?.data?.detail || "Checkout failed");
      setBusyId("");
    }
  }, []);

  // Auto-checkout if landed with ?buy=
  useEffect(() => {
    const sid = params.get("buy");
    if (sid && services.length > 0) {
      params.delete("buy"); setParams(params, { replace: true });
      buy(sid);
    }
    // eslint-disable-next-line
  }, [services]);

  if (!user) return null;

  return (
    <div data-testid="dashboard-page">
      <NavBar />
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <div className="label-eyebrow">DASHBOARD / 01</div>
            <h1 className="font-display text-4xl font-black uppercase tracking-tight mt-2">
              Welcome, {user.name || user.email}
            </h1>
            <div className="ribbon text-black/60 mt-2">{user.company || "Customer"}</div>
          </div>
          {params.get("cancelled") && (
            <div className="badge badge-pending" data-testid="dashboard-cancelled-banner">Last checkout cancelled</div>
          )}
        </div>

        <section className="mb-14">
          <h2 className="font-display text-2xl font-black uppercase tracking-tight mb-4">Place a new order</h2>
          {err && <div className="text-sm text-red-700 border border-red-700 p-2 mb-3" data-testid="dashboard-error">{err}</div>}
          {services.length === 0 ? (
            <div className="tech-card p-6 ribbon text-black/60" data-testid="dashboard-services-loading">
              Loading services…
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {services.map((s) => (
                <div key={s.id}
                     className="tech-card p-5 flex flex-col min-h-[210px] transition-transform hover:-translate-y-1"
                     data-testid={`service-tile-${s.id}`}>
                  <div className="ribbon text-black/60 mb-2">{s.category.toUpperCase()}</div>
                  <div className="font-display font-bold text-lg leading-tight">{s.name}</div>
                  <div className="text-xs text-black/60 mt-2">{s.description}</div>
                  <div className="mt-auto pt-4 flex items-center justify-between">
                    <div className="font-display font-black text-2xl">£{s.price.toFixed(0)}</div>
                    <button className="btn btn-sm btn-primary"
                            disabled={busyId === s.id}
                            onClick={() => buy(s.id)}
                            data-testid={`order-btn-${s.id}`}>
                      {busyId === s.id ? "..." : "Order →"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <section>
          <h2 className="font-display text-2xl font-black uppercase tracking-tight mb-4">My orders</h2>
          {orders.length === 0 ? (
            <p className="text-black/60 ribbon">No orders yet.</p>
          ) : (
            <div className="border border-black overflow-x-auto">
              <table className="w-full text-left border-collapse" data-testid="orders-table">
                <thead className="bg-black text-white">
                  <tr>
                    <th className="p-3 ribbon">Service</th>
                    <th className="p-3 ribbon">Amount</th>
                    <th className="p-3 ribbon">Status</th>
                    <th className="p-3 ribbon">Created</th>
                    <th className="p-3 ribbon"></th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((o) => {
                    const [cls, label] = STATUS_BADGE[o.status] || ["badge-default", o.status];
                    return (
                      <tr key={o.id} className="border-t border-black/15" data-testid={`order-row-${o.id}`}>
                        <td className="p-3 font-display font-bold">{o.service_name}</td>
                        <td className="p-3 font-display">£{o.amount.toFixed(2)}</td>
                        <td className="p-3"><span className={`badge ${cls}`}>{label}</span></td>
                        <td className="p-3 ribbon text-black/60">{new Date(o.created_at).toLocaleString()}</td>
                        <td className="p-3 text-right">
                          <Link to={`/orders/${o.id}`} className="ribbon underline" data-testid={`order-open-${o.id}`}>Open →</Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
